import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  RadioTower,
  BarChart3,
  ShieldCheck,
  Megaphone,
  Users,
  Smartphone,
  Tablet,
  MessagesSquare,
  Package,
  FileBarChart,
  Cable,
  LockKeyhole,
  UserCog,
  ScrollText,
  Settings as SettingsIcon,
  LogOut,
  Bell,
  ChevronDown,
  Menu as MenuIcon,
  X,
} from 'lucide-react';
import Dropdown, { DropdownItem } from '../components/ui/Dropdown';
import AuthService from '../services/AuthService';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { title: 'Admin Dashboard', path: '/dashboard', icon: LayoutGrid },
      { title: 'Command Center', path: '/command', icon: RadioTower },
      { title: 'High Level Dashboard', path: '/executive', icon: BarChart3 },
      { title: 'Team Leader', path: '/team-leader', icon: ShieldCheck },
    ],
  },
  {
    label: 'Operations',
    items: [
      { title: 'Announcements', path: '/activation', icon: Megaphone },
      { title: 'Teams Management', path: '/team', icon: Users },
      { title: 'Staff Mobile App', path: '/staff-app', icon: Smartphone },
      { title: 'Patient Documentation', path: '/patient-app', icon: Tablet },
      { title: 'Stakeholders', path: '/stakeholders', icon: MessagesSquare },
      { title: 'Inventory', path: '/inventory', icon: Package },
    ],
  },
  {
    label: 'System',
    items: [
      { title: 'Reports & KPI', path: '/reports', icon: FileBarChart },
      { title: 'User Management', path: '/users', icon: UserCog },
      { title: 'Audit Logs', path: '/audit', icon: ScrollText },
      { title: 'Connectivity', path: '/integrations', icon: Cable },
      { title: 'Governance', path: '/governance', icon: LockKeyhole },
      { title: 'Settings', path: '/settings', icon: SettingsIcon },
    ],
  },
];

function NavItem({ title, path, icon: Icon, onClick }) {
  return (
    <NavLink
      to={path}
      onClick={onClick}
      className={({ isActive }) =>
        `group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
          isActive ? 'bg-seha-500 text-white' : 'text-neutral-600 hover:bg-neutral-50 hover:text-seha-500'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`w-4 h-4 mr-3 ${isActive ? 'text-white' : 'text-neutral-400 group-hover:text-seha-500'}`} />
          {title}
        </>
      )}
    </NavLink>
  );
}

NavItem.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  onClick: PropTypes.func,
};

function SidebarNav({ onNavigate }) {
  return (
    <>
      <div className="h-20 flex items-center py-2 px-6 border-b border-neutral-100 shrink-0">
        <img src="/sehaLogo.jpeg" alt="SEHA Logo" className="py-0.5 h-16 mx-auto w-auto object-contain" />
      </div>
      <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-1.5">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <div className="px-2 mb-2 mt-6 first:mt-0 text-[10px] font-medium text-neutral-400 uppercase tracking-widest">
              {group.label}
            </div>
            <div className="space-y-1.5">
              {group.items.map((item) => (
                <NavItem key={item.path} {...item} onClick={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-4 border-t border-neutral-100 mt-auto shrink-0">
        <button
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-left text-red-600 hover:bg-red-50 border border-transparent"
          onClick={() => {
            AuthService.logout();
            window.location.href = '/login';
          }}
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </div>
    </>
  );
}

function Sidebar(props) {
  const { title, header, children } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="bg-neutral-50 text-neutral-600 h-screen flex overflow-hidden antialiased">
      <aside className="hidden lg:flex w-72 bg-white flex-col shrink-0 z-30 border-r border-neutral-100">
        <SidebarNav />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-neutral-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col">
            <SidebarNav onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-20 bg-white border-b border-neutral-100 flex items-center justify-between px-6 z-20 relative shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-neutral-400 hover:text-neutral-700" onClick={() => setMobileOpen((o) => !o)}>
              {mobileOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
            </button>
            <div className="flex flex-col min-w-0">
              <h2 className="text-xl font-bold text-neutral-900 leading-none truncate">{title}</h2>
              <span className="text-[11px] text-neutral-400 mt-1">Live Command</span>
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            {header}

            <div className="hidden xl:flex items-center gap-2 border-l border-neutral-100 pl-4">
              <span className="inline-flex items-center gap-2 rounded-full border border-green-100 bg-green-50 px-3 py-2 text-xs font-semibold text-green-700">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Live Sync
              </span>
            </div>

            <button className="p-2 text-neutral-400 hover:bg-neutral-50 hover:text-neutral-600 rounded-lg relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-red-500 rounded-full border border-white" />
            </button>

            <Dropdown
              trigger={({ toggle }) => (
                <button
                  onClick={toggle}
                  className="flex items-center gap-3 hover:bg-neutral-50 p-1.5 pr-2 rounded-lg transition-colors border border-transparent hover:border-neutral-100"
                >
                  <div className="w-10 h-10 rounded-full bg-seha-50 border border-seha-100 shadow-sm flex items-center justify-center text-seha-700 font-bold text-xs">
                    CC
                  </div>
                  <div className="hidden md:flex flex-col items-start">
                    <p className="font-semibold text-neutral-900 leading-tight text-sm">{AuthService.getUser()?.fullname || 'CBRNE Command'}</p>
                    <p className="text-xs text-neutral-400 font-medium capitalize">{AuthService.getUser()?.role || 'Admin'}</p>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-neutral-400" />
                </button>
              )}
            >
              {({ close }) => (
                <>
                  <div className="px-4 py-3 border-b border-neutral-50">
                    <p className="text-sm font-bold text-neutral-900">{AuthService.getUser()?.fullname || 'SEHA CBRNE Command'}</p>
                    <p className="text-xs text-neutral-500 truncate">{AuthService.getUser()?.email || ''}</p>
                  </div>
                  <DropdownItem
                    icon={LayoutGrid}
                    onClick={() => {
                      navigate('/dashboard');
                      close();
                    }}
                  >
                    Admin View
                  </DropdownItem>
                  <DropdownItem
                    icon={BarChart3}
                    onClick={() => {
                      navigate('/executive');
                      close();
                    }}
                  >
                    High Level View
                  </DropdownItem>
                  <DropdownItem
                    icon={ShieldCheck}
                    onClick={() => {
                      navigate('/team-leader');
                      close();
                    }}
                  >
                    Team Leader View
                  </DropdownItem>
                  <DropdownItem
                    icon={LogOut}
                    onClick={() => {
                      AuthService.logout();
                      window.location.href = '/login';
                    }}
                  >
                    Logout
                  </DropdownItem>
                </>
              )}
            </Dropdown>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-neutral-50 relative p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}

Sidebar.propTypes = {
  title: PropTypes.node,
  header: PropTypes.node,
};

export default Sidebar;
